import { useContext, useEffect, useState } from "react";
import { AppLayout } from "../../../containers/layout/app";
import { JournalEntryWrapper } from "./styled";
import { AppContext } from "../../../context/appContext";
import { Box, Grid2, Stack, Typography } from "@mui/material";
import { BaseButton } from "../../../components/button/styled";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Cookies from "universal-cookie";
import { retrieveAllJournalEntryService } from "../../../utils/api/accounting/journalentry/retrieveAllJournalEntry";
import { JournalEntryTable } from "../../../containers/tables/journalentrytable";

export const JournalEntry = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { setActiveNavItem } = useContext(AppContext);

	const [isFetching, setIsFetching] = useState(false);
	const [paginationIndex, setPaginationIndex] = useState({
		page: "1",
		perPage: "10",
		totalPages: "1",
	});

	useEffect(() => {
		setActiveNavItem("Accounting");
	}, [setActiveNavItem]);

	const { data: journalEntries } = useQuery({
		queryKey: ["journalEntries", TOKEN, paginationIndex],
		queryFn: async () => {
			setIsFetching(true);
			const response = await retrieveAllJournalEntryService(
				TOKEN,
				paginationIndex,
			);
			if (!Array.isArray(response) && response?.meta) {
				setPaginationIndex((prev) => ({
					...prev,
					totalPages: String(response?.meta?.totalPages ?? "1"),
				}));
			}
			setIsFetching(false);
			return response?.data ?? [];
		},
		enabled: !!TOKEN,
	});

	const handleAddJournalEntryButtonClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.preventDefault();
		return navigate("/accounting/addjournalentry");
	};

	const handlePagination = (
		e: React.MouseEvent<HTMLButtonElement>,
		type: "previous" | "next",
	) => {
		e.preventDefault();
		setPaginationIndex((prev) => {
			const currentPage = parseInt(prev.page, 10);
			const newPage =
				type === "previous"
					? Math.max(1, currentPage - 1)
					: Math.min(parseInt(prev.totalPages, 10), currentPage + 1);
			return {
				...prev,
				page: newPage.toString(),
			};
		});
	};

	return (
		<AppLayout pageId={"Journal-Entry"} pageTitle={"Journal Entry"}>
			<JournalEntryWrapper>
				<Box>
					<BaseButton
						variant="contained"
						bgcolor="var(--primary-color)"
						padding="calc(var(--basic-padding)/4) calc(var(--basic-padding))"
						startIcon={<AddIcon />}
						sx={{
							width: { mobile: "-webkit-fill-available", miniTablet: "auto" },
							float: { miniTablet: "right" },
						}}
						onClick={handleAddJournalEntryButtonClick}
					>
						<Typography
							variant={"button"}
							fontFamily={"inherit"}
							fontWeight={"inherit"}
							fontSize={"inherit"}
							lineHeight={"inherit"}
							color={"inherit"}
							textTransform={"inherit"}
						>
							Add Journal Entry
						</Typography>
					</BaseButton>
				</Box>
				<Box component={"div"} className="journal-entry-table-box">
					<JournalEntryTable rows={journalEntries ?? []} />
				</Box>
				<Grid2 container component={"div"} spacing={"var(--flex-gap)"}>
					<Grid2
						size={{ mobile: 12, laptop: 8 }}
						display={{ mobile: "none", laptop: "grid" }}
					></Grid2>
					<Grid2 size={{ mobile: 12, laptop: 4 }}>
						<Stack
							direction={"row"}
							overflow={"hidden"}
							borderRadius={"6px"}
							alignItems={"center"}
							justifyContent={"space-around"}
							border={"1px solid var(--input-field-border-color)"}
						>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									disabled={
										parseInt(paginationIndex.page, 10) === 1 || isFetching
									}
									onClick={(e) => handlePagination(e, "previous")}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Previous
									</Typography>
								</BaseButton>
							</Box>
							<Box
								display={"flex"}
								overflow={"hidden"}
								bgcolor={"var(--primary-color)"}
								padding={"calc(var(--basic-padding)/2)"}
							>
								<Typography
									variant="h3"
									fontFamily={"Roboto"}
									fontWeight={600}
									fontSize={10}
									lineHeight={"normal"}
									color="var(--light-color)"
								>
									{`${paginationIndex.page} / ${paginationIndex.totalPages}`}
								</Typography>
							</Box>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									onClick={(e) => handlePagination(e, "next")}
									disabled={
										paginationIndex.page === paginationIndex.totalPages ||
										isFetching
									}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Next
									</Typography>
								</BaseButton>
							</Box>
						</Stack>
					</Grid2>
				</Grid2>
			</JournalEntryWrapper>
		</AppLayout>
	);
};
